import React, { useState } from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  FiHome,
  FiPackage,
  FiUsers,
  FiDollarSign,
  FiTrendingUp,
  FiClock,
  FiCheckCircle,
  FiAlertCircle,
  FiPlus,
  FiEdit,
  FiEye,
  FiBarChart3,
  FiSettings,
} from "react-icons/fi";
import { vendorsAPI } from "../../services/api";

const VendorDashboardContainer = styled.div`
  padding: 2rem;
`;

const VendorDashboardContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const PageDescription = styled.div`
  margin-bottom: 2rem;
  text-align: center;

  p {
    color: #666;
    font-size: 1.125rem;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 1rem;

  .icon {
    width: 60px;
    height: 60px;
    border-radius: 12px;
    background: ${(props) => props.iconBg || "#2d5016"};
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.5rem;
  }

  .content {
    flex: 1;

    h3 {
      font-size: 2rem;
      font-weight: 700;
      color: #2d5016;
      margin: 0 0 0.25rem 0;
    }

    p {
      color: #666;
      margin: 0;
      font-size: 0.875rem;
    }

    .change {
      font-size: 0.75rem;
      font-weight: 600;
      margin-top: 0.25rem;

      &.positive {
        color: #28a745;
      }

      &.negative {
        color: #dc3545;
      }

      &.neutral {
        color: #6c757d;
      }
    }
  }
`;

const QuickActions = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;

  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #2d5016;
    margin-bottom: 1rem;
  }
`;

const ActionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  border: 2px solid #e0e0e0;
  background: white;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: left;

  &:hover {
    border-color: #2d5016;
    background: #f8f9fa;
    transform: translateY(-2px);
  }

  .icon {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: linear-gradient(135deg, #2d5016, #4a7c59);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.25rem;
  }

  .content {
    h3 {
      font-size: 1rem;
      font-weight: 600;
      color: #2d5016;
      margin: 0 0 0.25rem 0;
    }

    p {
      font-size: 0.875rem;
      color: #666;
      margin: 0;
    }
  }
`;

const RecentActivity = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #2d5016;
    margin-bottom: 1rem;
  }
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ActivityItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;

  .icon {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: ${(props) => props.iconBg || "#2d5016"};
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1rem;
  }

  .content {
    flex: 1;

    h4 {
      font-size: 0.875rem;
      font-weight: 600;
      color: #2d5016;
      margin: 0 0 0.25rem 0;
    }

    p {
      font-size: 0.75rem;
      color: #666;
      margin: 0;
    }
  }

  .time {
    font-size: 0.75rem;
    color: #999;
  }
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: 1.125rem;
  color: #666;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;

  h3 {
    margin-bottom: 1rem;
    color: #333;
  }
`;

const VendorDashboard = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const navigate = useNavigate();

  // Fetch vendor data
  const {
    data: vendorData,
    isLoading,
    error,
  } = useQuery("vendor-dashboard", () => vendorsAPI.getMyVendor(), {
    keepPreviousData: true,
  });

  const vendor = vendorData?.data?.data?.vendor || {};
  const statistics = vendorData?.data?.data?.statistics || {};

  // Debug: Log the statistics data
  console.log("Vendor Dashboard - Full Data:", vendorData);
  console.log("Vendor Dashboard - Statistics:", statistics);
  console.log(
    "Vendor Dashboard - Active Meal Plans:",
    statistics.active_meal_plans
  );

  // Stats from API data
  const stats = [
    {
      icon: <FiPackage />,
      iconBg: "#2d5016",
      label: "Active Meal Plans",
      value: statistics.active_meal_plans || 0,
      change: `${statistics.total_meal_plans || 0} total meal plans`,
      changeType: "neutral",
    },
    {
      icon: <FiUsers />,
      iconBg: "#4a7c59",
      label: "Total Subscribers",
      value: statistics.total_subscriptions || 0,
      change: `${statistics.total_orders || 0} total orders`,
      changeType: "neutral",
    },
    {
      icon: <FiDollarSign />,
      iconBg: "#28a745",
      label: "Monthly Revenue",
      value: `$${statistics.monthly_revenue || 0}`,
      change: "Last 30 days",
      changeType: "neutral",
    },
    {
      icon: <FiTrendingUp />,
      iconBg: "#17a2b8",
      label: "Average Rating",
      value: (statistics.average_rating || 0).toFixed(1),
      change: `Based on ${statistics.total_reviews || 0} reviews`,
      changeType: "neutral",
    },
  ];

  const quickActions = [
    {
      icon: <FiPlus />,
      title: "Add Meal Plan",
      description: "Create a new meal plan",
      onClick: () => navigate("/vendor/meal-plans/create"),
    },
    {
      icon: <FiEdit />,
      title: "Manage Menu",
      description: "Edit existing meal plans",
      onClick: () => navigate("/vendor/meal-plans"),
    },
    {
      icon: <FiEye />,
      title: "View Analytics",
      description: "Check performance metrics",
      onClick: () => navigate("/vendor/analytics"),
    },
    {
      icon: <FiSettings />,
      title: "Business Settings",
      description: "Update business information",
      onClick: () => navigate("/vendor/settings"),
    },
  ];

  const recentActivity = [
    {
      icon: <FiCheckCircle />,
      iconBg: "#28a745",
      title: "New customer for Weekly Guyanese Plan",
      description: "John Doe purchased your meal plan",
      time: "2 hours ago",
    },
    {
      icon: <FiPlus />,
      iconBg: "#2d5016",
      title: "Meal plan created",
      description: "Monthly Caribbean Plan added",
      time: "1 day ago",
    },
    {
      icon: <FiEdit />,
      iconBg: "#17a2b8",
      title: "Menu updated",
      description: "Updated pricing for Daily Specials",
      time: "3 days ago",
    },
    {
      icon: <FiAlertCircle />,
      iconBg: "#ffc107",
      title: "Review received",
      description: "New 5-star review from Sarah M.",
      time: "1 week ago",
    },
  ];

  if (isLoading) {
    return (
      <VendorDashboardContainer>
        <VendorDashboardContent>
          <LoadingState>
            <FiClock className="animate-spin" />
            Loading dashboard...
          </LoadingState>
        </VendorDashboardContent>
      </VendorDashboardContainer>
    );
  }

  if (error) {
    return (
      <VendorDashboardContainer>
        <VendorDashboardContent>
          <EmptyState>
            <h3>Error loading dashboard</h3>
            <p>{error.message}</p>
          </EmptyState>
        </VendorDashboardContent>
      </VendorDashboardContainer>
    );
  }

  return (
    <VendorDashboardContainer>
      <VendorDashboardContent>
        <PageDescription>
          <p>
            Welcome back, {vendor.business_name || "Vendor"}! Manage your meal
            plans and track your performance.
          </p>
        </PageDescription>

        <StatsGrid>
          {stats.map((stat, index) => (
            <StatCard
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              iconBg={stat.iconBg}
            >
              <div className="icon">{stat.icon}</div>
              <div className="content">
                <h3>{stat.value}</h3>
                <p>{stat.label}</p>
                <div className={`change ${stat.changeType}`}>{stat.change}</div>
              </div>
            </StatCard>
          ))}
        </StatsGrid>

        <QuickActions>
          <h2>Quick Actions</h2>
          <ActionGrid>
            {quickActions.map((action, index) => (
              <ActionButton
                key={index}
                onClick={action.onClick}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <div className="icon">{action.icon}</div>
                <div className="content">
                  <h3>{action.title}</h3>
                  <p>{action.description}</p>
                </div>
              </ActionButton>
            ))}
          </ActionGrid>
        </QuickActions>

        <RecentActivity>
          <h2>Recent Activity</h2>
          <ActivityList>
            {recentActivity.map((activity, index) => (
              <ActivityItem
                key={index}
                iconBg={activity.iconBg}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <div className="icon">{activity.icon}</div>
                <div className="content">
                  <h4>{activity.title}</h4>
                  <p>{activity.description}</p>
                </div>
                <div className="time">{activity.time}</div>
              </ActivityItem>
            ))}
          </ActivityList>
        </RecentActivity>
      </VendorDashboardContent>
    </VendorDashboardContainer>
  );
};

export default VendorDashboard;
